import { getMoment, showPeriodByDay, showLogNoTime } from '@libs/NwMoment';

export const SEASON_NO_NAME_LABEL = 'NO NAME';

export const getSeasonDisplayName = (seasonOrName) => {
    const name =
        typeof seasonOrName === 'object' && seasonOrName !== null ? seasonOrName.Name : seasonOrName;

    if (name === null || name === undefined) {
        return SEASON_NO_NAME_LABEL;
    }

    const trimmedName = String(name).trim();
    return trimmedName || SEASON_NO_NAME_LABEL;
};

export const getSeasonSortDate = (season) => season.StartDate || season.DateCreated;

export const sortSeasonsDesc = (seasons) => {
    return [...seasons].sort((a, b) => {
        const dateA = getMoment(getSeasonSortDate(a));
        const dateB = getMoment(getSeasonSortDate(b));

        if (!dateA.isValid() && !dateB.isValid()) {
            return 0;
        }
        if (!dateA.isValid()) {
            return 1;
        }
        if (!dateB.isValid()) {
            return -1;
        }

        return dateB.valueOf() - dateA.valueOf();
    });
};

export const formatSeasonPeriod = (season) => {
    if (season.StartDate && season.EndDate) {
        return showPeriodByDay(season.StartDate, season.EndDate);
    }
    if (season.StartDate) {
        return showPeriodByDay(season.StartDate, season.StartDate);
    }
    if (season.DateCreated) {
        return `Created ${showLogNoTime(season.DateCreated)}`;
    }
    return 'n.d.';
};

export const SEASON_MODEL_STATUS = {
    ACTIVE: 'Active',
    CANCELLED: 'Cancelled',
};

export const SEASON_LIST_FILTER = {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
};

export const MAX_ACTIVE_SEASONS = 6;

export const isSeasonArchived = (season) => !!(season?.Archive || season?.Archived);

export const normalizeSeason = (season) => {
    if (!season) {
        return null;
    }

    const archived = isSeasonArchived(season);

    return {
        ...season,
        Archive: archived,
        Archived: archived,
    };
};

export const normalizeSeasonModel = (seasonModel, seasonId) => ({
    ...seasonModel,
    SeasonID: seasonModel.SeasonID ?? seasonId,
});

export const normalizeSeasonDetails = (details) => {
    if (!details) {
        return null;
    }

    const season = normalizeSeason(details);

    return {
        ...season,
        Models: (details.Models || []).map((seasonModel) => normalizeSeasonModel(seasonModel, season.ID)),
    };
};

export const filterSeasonsByArchive = (seasons, filter) => {
    if (filter === SEASON_LIST_FILTER.ARCHIVED) {
        return seasons.filter(isSeasonArchived);
    }
    return seasons.filter((season) => !isSeasonArchived(season));
};

export const normalizeDepartmentId = (departmentId) => {
    if (departmentId === null || departmentId === undefined || departmentId === '') {
        return null;
    }

    const normalizedId = Number(departmentId);
    return Number.isNaN(normalizedId) ? null : normalizedId;
};

export const isSameDepartmentId = (leftId, rightId) => {
    const normalizedLeftId = normalizeDepartmentId(leftId);
    const normalizedRightId = normalizeDepartmentId(rightId);

    return normalizedLeftId !== null && normalizedRightId !== null && normalizedLeftId === normalizedRightId;
};

export const isDepartmentInActiveList = (departmentId, activeDepartments) => {
    if (!activeDepartments?.length) {
        return false;
    }

    const normalizedDepartmentId = normalizeDepartmentId(departmentId);
    if (normalizedDepartmentId === null) {
        return false;
    }

    return activeDepartments.some((activeDepartmentId) => isSameDepartmentId(activeDepartmentId, normalizedDepartmentId));
};

export const isSeasonInActiveContext = (season, activeContext) => {
    if (!season || !activeContext) {
        return false;
    }

    if (activeContext.nwAgId != null && season.NetworkAgencyCreated != null) {
        if (!isSameDepartmentId(season.NetworkAgencyCreated, activeContext.nwAgId)) {
            return false;
        }
    }

    return isDepartmentInActiveList(season.DepartmentCreated, activeContext.activeDepartments);
};

export const filterSeasonsByActiveContext = (seasons, activeContext) => {
    if (!activeContext?.activeDepartments?.length) {
        return [];
    }

    return seasons.filter((season) => isSeasonInActiveContext(season, activeContext));
};

export const countActiveSeasonsInContext = (seasons, activeContext) => {
    return filterSeasonsByActiveContext(seasons, activeContext).filter((season) => !isSeasonArchived(season)).length;
};

export const canCreateSeason = (seasons, activeContext) => {
    return countActiveSeasonsInContext(seasons, activeContext) < MAX_ACTIVE_SEASONS;
};

export const filterSeasonsByDepartment = (seasons, departmentId) => {
    if (!departmentId) {
        return seasons;
    }

    return seasons.filter((season) => isSameDepartmentId(season.DepartmentCreated, departmentId));
};

export const isSeasonModelCancelled = (seasonModel) => seasonModel?.Status === SEASON_MODEL_STATUS.CANCELLED;

export const getActiveSeasonModelIds = (seasonModels) =>
    (seasonModels || [])
        .filter((seasonModel) => !isSeasonModelCancelled(seasonModel))
        .map((seasonModel) => seasonModel.ModelID);

export const toActiveShowSeason = (season, modelIds = []) => ({
    ID: season.ID,
    Name: season.Name,
    StartDate: season.StartDate,
    EndDate: season.EndDate,
    DepartmentCreated: season.DepartmentCreated,
    Models: modelIds.map((modelId) => Number(modelId)),
});

export const buildSeasonModelsByModelId = (seasonDetails) => {
    const seasonModelsByModelId = new Map();

    (seasonDetails?.Models || []).forEach((seasonModel) => {
        seasonModelsByModelId.set(Number(seasonModel.ModelID), seasonModel);
    });

    return seasonModelsByModelId;
};
